import React, { useState, useMemo, useCallback, ComponentType, useEffect } from 'react';
import { useMatch } from 'react-router-dom';
import { debounce } from 'lodash';

import { SearchField } from '@ocx/ui';
import { AppRoutePath } from '@ocx-app/pages/AppRoutePath';
import { useGetNavigationActions } from '@ocx-app/hooks/useGetNavigationActions';
import { useRudderStack } from '@ocx-app/lib/rudderStack/useRudderStack';
import { useConfiguration } from '@ocx-app/modules/configuration/useConfiguration';

import { useOptions } from './hooks/use-options.hook';
import { useStoreLocatorController } from './hooks/use-store-locator-controller.hook';
import { StoreLocator } from './store-locator.component';
import { StoreLocatorLayout } from './store-locator.layout';
import { useCurrentLocation } from './hooks/use-current-location.hook';
import { ListView } from './components/list-view.component';
import { useMapNavigation } from './hooks/use-map-navigation.hook';
import { MapToolbar } from './components/map-toolbar.component';
import { Loading } from './components/loading.component';
import { MAP_POSITION_CHANGE_TIMEOUT } from './store-locator.constants';

type WithLocationProps = {
  location: ReturnType<typeof useCurrentLocation>;
};

const maxPendingLimit = 5000; // pending ime limit if user in prompt permission status

/*
  Necessary to prevent API requests with default location if we have access to user location.
  Wait until it becomes clear whether the user’s location is available or not.
  Or before timeout prevents waiting.
*/
const withLocation = (Component: ComponentType<WithLocationProps>) => {
  const WithExtraInfo: React.FC = (props) => {
    const location = useCurrentLocation();
    const [pending, sePending] = useState(location.isLocationPending);

    useEffect(() => {
      if (!location.isLocationPending) {
        sePending(false);
      }
      const timer = setTimeout(() => {
        sePending(false);
      }, maxPendingLimit);
      return () => clearTimeout(timer);
    }, [location.isLocationPending]);

    if (pending) {
      return <Loading open />;
    }
    return <Component {...props} location={location} />;
  };

  return WithExtraInfo;
};

const StoreLocatorController: React.FC<WithLocationProps> = ({ location }) => {
  const { currentLocation, isLocationEstablished, isLocationPermissionDenied } = location;

  const { pushToStoreDetails } = useGetNavigationActions();
  // eslint-disable-next-line no-undef
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const options = useOptions();
  const { triggerEvent } = useRudderStack();
  const googleMapsApiKey = useConfiguration('storeLocator.googleMapsApiKey');

  const {
    search,
    selectedStore,
    targetLocation,
    handleSelectStore,
    navigateToListView,
    navigateToMapView,
    navigateToPosition,
    moveToCurrentLocation,
    navigateWithSearch,
    openLocationSettings,
    locationNotFound,
    setSelectedStore,
    specificStoreUUID,
  } = useMapNavigation(map, currentLocation, isLocationEstablished);

  const { stores, loading, isLoadMoreAvailable, handleLoadMore, handleZoom } = useStoreLocatorController({
    map,
    targetLocation,
    currentLocation,
    navigateToPosition,
    setSelectedStore,
    specificStoreUUID,
  });

  const [hideNoStores, setHideNoStores] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const onDragStart = useCallback(() => {
    setHideNoStores(true);
    setIsDragging(true);
  }, []);

  const onDragEnd = useCallback(() => {
    debounce(() => {
      navigateToPosition();
      setIsDragging(false);
    }, MAP_POSITION_CHANGE_TIMEOUT)();
  }, [navigateToPosition]);

  const isNoStoresFound = useMemo(() => {
    if (hideNoStores || loading || !map) {
      return false;
    }
    if (locationNotFound) {
      return true;
    }
    return !stores.length;
  }, [hideNoStores, loading, locationNotFound, stores.length, map]);

  const isInListView = useMatch(AppRoutePath.StoreLocatorList);
  const handleStoreClick = useCallback(
    (storeId: string) => {
      triggerEvent('store_locator_map_selected_store_clicked', { uuid: storeId });
      pushToStoreDetails(storeId);
    },
    [pushToStoreDetails, triggerEvent],
  );

  const onSubmit = useCallback(
    (value: string) => {
      triggerEvent('store_locator_search_submit', { search: value });
      navigateWithSearch(value);
      setHideNoStores(false);
    },
    [navigateWithSearch, triggerEvent],
  );

  return (
    <StoreLocatorLayout>
      <MapToolbar>
        <SearchField onSubmit={onSubmit} value={search} />
      </MapToolbar>
      <StoreLocator
        options={options}
        googleMapsApiKey={googleMapsApiKey}
        loading={loading}
        stores={stores}
        currentLocation={currentLocation}
        targetLocation={targetLocation}
        selectedStore={selectedStore}
        isNoStoresFound={isNoStoresFound}
        isLocationGranted={!isLocationPermissionDenied}
        onMapLoaded={setMap}
        onStorePinClick={handleSelectStore}
        onNavigateToListViewClick={navigateToListView}
        onStoreClick={handleStoreClick}
        onZoomChanged={handleZoom}
        onCurrentLocationClick={isLocationPermissionDenied ? openLocationSettings : moveToCurrentLocation}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        disabledControls={isDragging || loading}
      />
      {isInListView && (
        <ListView
          loading={loading}
          stores={stores}
          isLocationGranted={!isLocationPermissionDenied}
          isLoadMoreAvailable={isLoadMoreAvailable}
          onMapViewClick={navigateToMapView}
          onStoreClick={pushToStoreDetails}
          onLoadMoreClick={handleLoadMore}
          isNoStoresFound={isNoStoresFound}
        />
      )}
    </StoreLocatorLayout>
  );
};

export const StoreLocatorControllerWithLocation = withLocation(StoreLocatorController);
